import { Box } from "@mui/material";
import React from "react";
import useTheme from "../../../../../../../common/hooks/useTheme";
import { Theme } from "../../../../../../../providers/ThemeProvider";
import { equipmentLabel } from "../../model/workoutEnums";
import EquipmentIcon from "../shared/EquipmentIcon";

type Props = {
  /** Equipment ids in use, "None" already excluded. */
  equipment: number[];
};

/**
 * The mobile `WorkoutEquipmentWidget`: a framed "Equipment" card holding one
 * grey tile per piece of equipment (glyph above the name), two per row. The
 * web shows every tile instead of the mobile's two-plus-expand.
 */
const EquipmentWidget = ({ equipment }: Props) => {
  const theme: Theme = useTheme();
  if (equipment.length === 0) return null;

  return (
    <Box sx={styles.container(theme)}>
      <Box sx={styles.title(theme)}>Equipment</Box>
      <Box sx={styles.grid}>
        {equipment.map((id) => (
          <Box key={id} sx={styles.tile(theme)}>
            <EquipmentIcon equipment={id} fill={theme.colors_ds.NEUTRAL_TEXT_STRONG} />
            <Box sx={styles.tileLabel(theme)}>{equipmentLabel(id)}</Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default EquipmentWidget;

const styles = {
  container: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    gap: "16px",
    padding: "12px",
    borderRadius: "8px",
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
  }),
  title: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
  }),
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "8px",
  },
  tile: (theme: Theme) => ({
    display: "flex",
    flexDirection: "column" as "column",
    justifyContent: "space-between",
    height: "76px",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_ELEVATION1,
  }),
  tileLabel: (theme: Theme) => ({
    color: theme.colors_ds.NEUTRAL_TEXT_STRONG,
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "20px",
    whiteSpace: "nowrap" as "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};
